'use client'

/* ---------------------------------
PlaceLocator
--------------------------------- */

import React, { useEffect } from 'react'
import Locator, {
  AutocompleteItemSelectTrackingData,
  CTATrackingData,
  LocatorProps,
  Map,
  Search,
  TrackingEvent,
} from '@dynamo-caffeina-dev/locator-frontend'
import '@dynamo-caffeina-dev/locator-frontend/styles'
import { NormalizedSection } from '@/types'
import {
  Icons,
  OpeningTitle,
  Paragraph,
  Section,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import { dispatchGTMEvent, GTM_EVENT_CATEGORY } from '@/helpers/gtm'

// TODO
// export the config from Locator
// as a standalone type
type __LocatorConfig__ = Omit<
  LocatorProps,
  | 'locations'
  | 'fetchLocations'
  | 'customIcons'
  | 'customComponents'
  | 'trackingConfig'
>

export default function PlaceLocator({
  modelledContent: extractedContent,
}: NormalizedSection): React.JSX.Element {
  const {
    openingTitle,
    title,
    paragraph,
    locatorConfig: config,
  } = extractedContent ?? {}

  useEffect(() => {
    const styleElement = document.createElement('style')
    // We need to set `--dyn-locator__colors-accent-500` on `:root`,
    // because the map `Marker` looks for it in `document.documentElement`.
    styleElement.textContent = `
  :root {
    --dyn-locator__colors-accent-100: var(--airc-ui__colors-accent-200);
    --dyn-locator__colors-accent-200: var(--airc-ui__colors-accent-200);
    --dyn-locator__colors-accent-300: var(--airc-ui__colors-accent-300);
    --dyn-locator__colors-accent-400: var(--airc-ui__colors-accent-400);
    --dyn-locator__colors-accent-500: var(--airc-ui__colors-accent-500);
    --dyn-locator__colors-accent-600: var(--airc-ui__colors-accent-600);
    --dyn-locator__colors-accent-700: var(--airc-ui__colors-accent-700);
    --dyn-locator__colors-accent-800: var(--airc-ui__colors-accent-800);
    --dyn-locator__colors-accent-900: var(--airc-ui__colors-accent-800);
  }
`
    document.head.appendChild(styleElement)
  }, [])

  return (
    <Section
      id={'place-locator'}
      className='section-place-locator'
      style={{
        // Font overrides
        // @ts-ignore
        '--dyn-locator__font-stack':
          "var(--font-graphik), -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
      }}
    >
      <div className='section-place-locator__grid'>
        <div className='section-intro section-place-locator__intro-text'>
          <OpeningTitle {...openingTitle}>{openingTitle.content}</OpeningTitle>

          <div className='section-intro--content'>
            <Title
              {...title}
              dangerouslySetInnerHTML={{ __html: title.content || '' }}
            />
            <Paragraph
              as={'div'}
              dangerouslySetInnerHTML={{ __html: paragraph.content || '' }}
            />
          </div>
        </div>

        <div className='section-place-locator__widget-container'>
          <Locator
            //
            // CMS CONFIG
            //
            {...(config as __LocatorConfig__)}
            //
            // CUSTOM LABELS
            //
            localizationConfig={{
              lang: 'it',
              labels: {
                'Search.placeholder': 'Il tuo indirizzo',
              },
            }}
            //
            // CUSTOM UI
            //
            // TODO
            // icons are no longer memoized in Locator,
            // but they are in the UI Kit
            customIcons={{
              Share:
                Icons.Share as unknown as React.ForwardRefExoticComponent<any>,
              Expand:
                Icons.Expand as unknown as React.ForwardRefExoticComponent<any>,
            }}
            //
            // TRACKING
            //
            trackingConfig={{
              [TrackingEvent.AUTOCOMPLETE_ITEM_SELECT]: {
                enabled: true,
                tracker: (data) =>
                  dispatchGTMEvent(GTM_EVENT_CATEGORY.QUERY_LOCATION, {
                    search_type: (
                      data as unknown as AutocompleteItemSelectTrackingData
                    ).province,
                  }),
              },

              [TrackingEvent.LOCATION_CARD_PRIMARY_ACTION]: {
                enabled: true,
                tracker: (data) =>
                  dispatchGTMEvent(GTM_EVENT_CATEGORY.CTA_CLICK, {
                    cta_clicked: (data as unknown as CTATrackingData).cta_label,
                  }),
              },

              [TrackingEvent.LOCATION_CARD_SECONDARY_ACTION]: {
                enabled: true,
                tracker: (data) =>
                  dispatchGTMEvent(GTM_EVENT_CATEGORY.CTA_CLICK, {
                    cta_clicked: (data as unknown as CTATrackingData).cta_label,
                  }),
              },
            }}
          >
            <Search />
            <Map />
          </Locator>
        </div>
      </div>
    </Section>
  )
}
